import { motion } from 'framer-motion'
import { cn } from '@/utils'

export type NikiAvatarState = 'idle' | 'thinking' | 'speaking' | 'listening'

interface NikiAvatarProps {
  size?: 'sm' | 'md' | 'lg' | 'hero'
  state?: NikiAvatarState
  className?: string
  showRipples?: boolean
}

const sizeMap = {
  sm: { dimension: 40, fontSize: 16, ringSize: 4 },
  md: { dimension: 56, fontSize: 22, ringSize: 5 },
  lg: { dimension: 88, fontSize: 36, ringSize: 6 },
  hero: { dimension: 140, fontSize: 56, ringSize: 8 },
}

export function NikiAvatar({ size = 'md', state = 'idle', className, showRipples = true }: NikiAvatarProps) {
  const { dimension, fontSize, ringSize } = sizeMap[size]

  const getStateClass = () => {
    switch (state) {
      case 'idle':
        return 'avatar-glow-idle'
      case 'thinking':
        return 'avatar-glow-thinking'
      case 'speaking':
        return 'avatar-glow-speaking'
      case 'listening':
        return 'avatar-glow-listening'
      default:
        return ''
    }
  }

  return (
    <div className={cn('relative flex items-center justify-center', className)}>
      {/* Outer glow rings for listening state */}
      {state === 'listening' && showRipples && (
        <>
          <motion.div
            className="absolute rounded-full border-2 border-danger/50 listening-ring"
            style={{
              width: dimension + 20,
              height: dimension + 20,
            }}
          />
          <motion.div
            className="absolute rounded-full border-2 border-danger/30 listening-ring"
            style={{
              width: dimension + 20,
              height: dimension + 20,
              animationDelay: '0.5s',
            }}
          />
        </>
      )}

      {/* Speaking wave circles */}
      {state === 'speaking' && showRipples && (
        <>
          <motion.div
            className="absolute rounded-full"
            style={{
              width: dimension + 16,
              height: dimension + 16,
              background: 'radial-gradient(circle, rgba(16, 185, 129, 0.3) 0%, transparent 70%)',
            }}
            animate={{ scale: [1, 1.3, 1], opacity: [0.6, 0, 0.6] }}
            transition={{ duration: 1.2, repeat: Infinity }}
          />
          <motion.div
            className="absolute rounded-full"
            style={{
              width: dimension + 30,
              height: dimension + 30,
              background: 'radial-gradient(circle, rgba(16, 185, 129, 0.2) 0%, transparent 70%)',
            }}
            animate={{ scale: [1, 1.4, 1], opacity: [0.4, 0, 0.4] }}
            transition={{ duration: 1.4, repeat: Infinity, delay: 0.2 }}
          />
        </>
      )}

      {/* Thinking pulse effect */}
      {state === 'thinking' && showRipples && (
        <motion.div
          className="absolute rounded-full"
          style={{
            width: dimension + 20,
            height: dimension + 20,
            background: 'radial-gradient(circle, rgba(52, 211, 153, 0.4) 0%, transparent 70%)',
          }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        />
      )}

      {/* Main Avatar with bounce animation */}
      <motion.div
        className={cn(
          'relative rounded-full overflow-hidden flex-shrink-0',
          getStateClass()
        )}
        style={{ width: dimension, height: dimension }}
        animate={
          state === 'idle'
            ? {
                y: [0, -4, 0],
                scale: [1, 1.02, 1],
              }
            : state === 'thinking'
            ? {
                scale: [1, 1.05, 1.02, 1.05, 1],
              }
            : {}
        }
        transition={
          state === 'idle'
            ? {
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut',
              }
            : state === 'thinking'
            ? {
                duration: 1.2,
                repeat: Infinity,
                ease: 'easeInOut',
              }
            : {}
        }
      >
        {/* Safaricom green gradient background */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, #72E05B 0%, #4CD137 50%, #39B54A 100%)',
          }}
        />

        {/* Glass overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)',
          }}
        />

        {/* Animated shine */}
        {state === 'idle' && (
          <motion.div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.4) 50%, transparent 70%)',
              backgroundSize: '200% 200%',
            }}
            animate={{
              backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'],
            }}
            transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
          />
        )}

        {/* Niki Letter/Logo - Cream/Gold for premium look on bright green */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className="font-bold drop-shadow-lg"
            style={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontSize: fontSize,
              color: '#FFF8DC',
              textShadow: '0 2px 4px rgba(0,0,0,0.4), 0 0 8px rgba(212,175,55,0.3)',
            }}
          >
            N
          </span>
        </div>

        {/* Inner highlight */}
        <div
          className="absolute top-0 left-1/4 right-1/4 h-1/3 rounded-b-full"
          style={{
            background: 'linear-gradient(to bottom, rgba(255,255,255,0.4), transparent)',
          }}
        />
      </motion.div>

      {/* Audio wave bars for speaking state */}
      {state === 'speaking' && (
        <div
          className="absolute bottom-0 flex items-end justify-center gap-0.5"
          style={{ height: dimension / 4 }}
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <motion.div
              key={i}
              className="bg-emerald-400 rounded-full wave-bar"
              style={{
                width: ringSize - 2,
                height: '100%',
                animationDelay: `${i * 0.1}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Status ring */}
      <div
        className={cn(
          'absolute inset-0 rounded-full border-2 transition-colors duration-300',
          state === 'idle' && 'border-emerald-500/30',
          state === 'thinking' && 'border-emerald-400/50',
          state === 'speaking' && 'border-emerald-500/50',
          state === 'listening' && 'border-danger/60'
        )}
        style={{
          width: dimension + ringSize,
          height: dimension + ringSize,
          top: -ringSize / 2,
          left: -ringSize / 2,
        }}
      />
    </div>
  )
}

// Mini avatar for message bubbles
export function NikiAvatarMini({ state = 'idle' }: { state?: NikiAvatarState }) {
  return (
    <div className="relative w-8 h-8 rounded-full overflow-hidden flex-shrink-0 shadow-lg">
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, #72E05B 0%, #4CD137 100%)',
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className="font-bold text-sm"
          style={{
            fontFamily: 'Space Grotesk, sans-serif',
            color: '#1A4D1A',
            textShadow: '0 1px 2px rgba(255,255,255,0.3)',
          }}
        >
          N
        </span>
      </div>
      {state === 'thinking' && (
        <motion.div
          className="absolute inset-0 bg-emerald-400/30"
          animate={{ opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 1, repeat: Infinity }}
        />
      )}
    </div>
  )
}
